import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Save, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { integrationService, IntegrationCreate } from '@/services/integrationService';

export function IntegrationSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [integrationId, setIntegrationId] = useState<string | null>(null);
  const [showSubscriptionKey, setShowSubscriptionKey] = useState(false);
  const [showApplicationToken, setShowApplicationToken] = useState(false);
  
  const [formData, setFormData] = useState<Omit<IntegrationCreate, 'product'>>({
    adminUser: '',
    subscriptionId: '',
    subscriptionKey: '',
    applicationToken: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadIntegration();
  }, []);

  const loadIntegration = async () => {
    try {
      setLoading(true);
      const integrations = await integrationService.getAll();
      
      if (integrations.length > 0) {
        const integration = integrations[0];
        setIntegrationId(integration.id);
        setFormData({
          adminUser: integration.adminUser,
          subscriptionId: integration.subscriptionId,
          subscriptionKey: integration.subscriptionKey,
          applicationToken: integration.applicationToken,
        });
      }
    } catch (error) {
      console.error('Failed to load integration:', error);
      toast.error('Failed to load integration settings');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.adminUser.trim()) {
      newErrors.adminUser = 'User is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminUser)) {
      newErrors.adminUser = 'Please enter a valid email address';
    }

    if (!formData.subscriptionId.trim()) {
      newErrors.subscriptionId = 'Subscription ID is required';
    } else {
      const guidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!guidRegex.test(formData.subscriptionId)) {
        newErrors.subscriptionId = 'Please enter a valid GUID format';
      }
    }

    if (!formData.subscriptionKey.trim()) {
      newErrors.subscriptionKey = 'Subscription Key is required';
    }

    if (!formData.applicationToken.trim()) {
      newErrors.applicationToken = 'Application Token is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    try {
      setSaving(true);

      const integrationData: IntegrationCreate = {
        ...formData,
        product: 'Expense',
      };

      if (integrationId) {
        await integrationService.update(integrationId, integrationData);
        toast.success('Integration settings updated successfully');
      } else {
        const created = await integrationService.create(integrationData);
        setIntegrationId(created.id);
        toast.success('Integration settings saved successfully');
      }
    } catch (error) {
      console.error('Failed to save integration:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save integration settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof Omit<IntegrationCreate, 'product'>, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="adminUser">
            User <span className="text-destructive">*</span>
          </Label>
          <Input
            id="adminUser"
            type="text"
            placeholder="admin@expenseorg.com"
            value={formData.adminUser}
            onChange={(e) => handleInputChange('adminUser', e.target.value)}
            className={errors.adminUser ? 'border-destructive' : ''}
          />
          {errors.adminUser && (
            <p className="text-sm text-destructive">{errors.adminUser}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="subscriptionId">
            Subscription Id <span className="text-destructive">*</span>
          </Label>
          <Input
            id="subscriptionId"
            type="text"
            placeholder="00000000-0000-0000-0000-000000000000"
            value={formData.subscriptionId}
            onChange={(e) => handleInputChange('subscriptionId', e.target.value)}
            className={errors.subscriptionId ? 'border-destructive' : ''}
          />
          {errors.subscriptionId && (
            <p className="text-sm text-destructive">{errors.subscriptionId}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="subscriptionKey">
            Subscription Key <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <Input
              id="subscriptionKey"
              type={showSubscriptionKey ? 'text' : 'password'}
              placeholder="Enter subscription key"
              value={formData.subscriptionKey}
              onChange={(e) => handleInputChange('subscriptionKey', e.target.value)}
              className={errors.subscriptionKey ? 'border-destructive pr-10' : 'pr-10'}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
              onClick={() => setShowSubscriptionKey(!showSubscriptionKey)}
            >
              {showSubscriptionKey ? (
                <EyeOff className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Eye className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
          </div>
          {errors.subscriptionKey && (
            <p className="text-sm text-destructive">{errors.subscriptionKey}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="applicationToken">
            Application Token <span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <Input
              id="applicationToken"
              type={showApplicationToken ? 'text' : 'password'}
              placeholder="Enter application token"
              value={formData.applicationToken}
              onChange={(e) => handleInputChange('applicationToken', e.target.value)}
              className={errors.applicationToken ? 'border-destructive pr-10' : 'pr-10'}
            />
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
              onClick={() => setShowApplicationToken(!showApplicationToken)}
            >
              {showApplicationToken ? (
                <EyeOff className="h-4 w-4 text-muted-foreground" />
              ) : (
                <Eye className="h-4 w-4 text-muted-foreground" />
              )}
            </Button>
          </div>
          {errors.applicationToken && (
            <p className="text-sm text-destructive">{errors.applicationToken}</p>
          )}
        </div>
      </div>

      <div className="p-4 border rounded-lg bg-blue-50 dark:bg-blue-950/20">
        <p className="text-sm text-blue-900 dark:text-blue-100">
          <strong>Note:</strong> These credentials are used to integrate with your expense management system.
          All sensitive information is securely stored and encrypted.
        </p>
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
